import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['modal'];
  static values = {
    icon: String, path: String,
    iconClass: { type: String, default: '' },
    method: { type: String, default: 'patch' },
    title: { type: String, default: 'Please confirm' },
    message: { type: String, default: 'Are you sure?' },
    button: { type: String, default: 'Confirm' },
    cancel: { type: String, default: 'Cancel' },
    destructive: { type: Boolean, default: false },
    refresh: { type: Boolean, default: false },
    submitForm: { type: Boolean, default: false },
    checkboxLabel: { type: String, default: '' },
    checkboxChecked: { type: Boolean, default: true },
    checkboxParam: { type: String, default: '' },
  }

  connect() {
    this.modal = document.getElementById("confirm")
    this.icon = this.modal.querySelector("#confirm-icon")
    this.title = this.modal.querySelector("#confirm-title")
    this.message = this.modal.querySelector("#confirm-message")
    this.button = this.modal.querySelector(".confirm-button")
    this.cancel = this.modal.querySelector(".cancel-button")
    this.checkboxContainer = this.modal.querySelector("#confirm-checkbox-container")
    this.checkboxInput = this.modal.querySelector("#confirm-checkbox-input")
  }

  open(event) {
    // Only preventDefault if it's a click event, not our custom event
    if (event.type === 'click') {
      event.preventDefault()
    }

    // Check if the path was rewritten by decline-quote controller
    if (event.detail?.confirmPath) {
      this.pathValue = event.detail.confirmPath;
    }

    this.triggerElement = event.currentTarget || event.target
    const form = this.triggerElement.closest("form")
    const shouldSubmitForm = this.submitFormValue

    this.icon.innerHTML = this.iconValue
    this.icon.classList = this.iconClassValue
    this.title.textContent = this.titleValue
    this.message.textContent = this.messageValue
    this.cancel.textContent = this.cancelValue
    this.button.textContent = this.buttonValue
    this.button.dataset.confirmRefreshValue = this.refreshValue

    this.setupCheckbox()

    if (shouldSubmitForm && form) {
      this.button.dataset.formId = form.id || this.createFormId(form)
      this.button.dataset.shouldSubmitForm = 'true'
      delete this.button.dataset.url
      delete this.button.dataset.method
    } else {
      this.button.dataset.shouldSubmitForm = 'false'
      delete this.button.dataset.formId
      this.button.dataset.url = this.buildPath()
      const method = this.methodValue.toLowerCase()
      if (method === 'get') {
        delete this.button.dataset.method
      } else {
        this.button.dataset.method = method
      }
    }

    this.toggleDestructiveStyle(this.destructiveValue)
    this.showModal()
  }

  createFormId(form) {
    const formId = 'confirm-form-' + Math.random().toString(36).substring(2, 11)
    form.id = formId
    return formId
  }

  request({ title = 'Please confirm', message = 'Are you sure?', confirmButton = 'Confirm', destructive = false } = {}) {
    return new Promise((resolve) => {
      this.resolve = resolve

      this.icon.innerHTML = this.modal.querySelector('#confirm-default-icon').innerHTML
      this.icon.classList = ''
      this.title.textContent = title
      this.message.innerHTML = message
      this.cancel.textContent = 'Cancel'
      this.button.textContent = confirmButton

      this.button.dataset.confirmRefreshValue = 'false'
      this.button.dataset.shouldSubmitForm = 'false'
      delete this.button.dataset.formId
      delete this.button.dataset.url
      delete this.button.dataset.method

      this.toggleDestructiveStyle(destructive)
      this.checkboxContainer.classList.add('hidden')

      this.showModal()
    })
  }

  toggleDestructiveStyle(destructive) {
    if (destructive) {
      this.button.classList.remove("bg-brand-1000", "focus:ring-brand-200")
      this.button.classList.add("bg-red-1000", "focus:ring-red-200")
    } else {
      this.button.classList.remove("bg-red-1000", "focus:ring-red-200")
      this.button.classList.add("bg-brand-1000", "focus:ring-brand-200")
    }
  }

  close() {
    this.hideModal()
    this.cleanupCheckbox()
    this.resolve?.(false)
  }

  closeIfBackdrop(event) {
    if (event.target.dataset.modalBackdrop !== undefined) {
      this.hideModal()
      this.resolve?.(false)
    }
  }

  confirm(event) {
    event?.preventDefault()
    this.hideModal()
    this.cleanupCheckbox()

    const shouldSubmitForm = this.button.dataset.shouldSubmitForm === 'true'
    const formId = this.button.dataset.formId

    if (shouldSubmitForm && formId) {
      const form = document.getElementById(formId)
      if (form) {
        form.requestSubmit()
        return
      }
    }

    const url = this.button.dataset.url
    const method = this.button.dataset.method

    if (url && method) {
      this.submitMethodForm(url, method)
    } else if (url) {
      if (typeof Turbo !== 'undefined' && Turbo.visit) {
        Turbo.visit(url)
      } else {
        window.location.href = url
      }
      return
    } else {
      this.resolve?.(true)
    }

    if (this.button.dataset.confirmRefreshValue === "true") {
      setTimeout(() => {
        window.location.reload()
      }, 300)
    }
  }

  submitMethodForm(url, method) {
    const form = document.createElement('form')
    form.method = 'post'
    form.action = url
    form.style.display = 'none'

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const fields = {
      authenticity_token: csrfToken,
      _method: method,
    }

    Object.entries(fields).forEach(([name, value]) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value || ''
      form.appendChild(input)
    })

    document.body.appendChild(form)
    form.requestSubmit()
    form.remove()
  }

  setupCheckbox() {
    if (this.checkboxChangeHandler) {
      this.checkboxInput.removeEventListener('change', this.checkboxChangeHandler)
      this.checkboxChangeHandler = null
    }

    if (this.checkboxLabelValue) {
      this.checkboxContainer.classList.remove('hidden')
      this.checkboxContainer.querySelector('span').textContent = this.checkboxLabelValue
      this.checkboxInput.checked = this.checkboxCheckedValue
      this.checkboxChangeHandler = () => {
        this.button.dataset.url = this.buildPath()
      }
      this.checkboxInput.addEventListener('change', this.checkboxChangeHandler)
    } else {
      this.checkboxContainer.classList.add('hidden')
    }
  }

  buildPath() {
    if (this.checkboxParamValue && this.checkboxLabelValue && this.checkboxInput.checked) {
      const separator = this.pathValue.includes('?') ? '&' : '?'
      return `${this.pathValue}${separator}${this.checkboxParamValue}=true`
    }
    return this.pathValue
  }

  cleanupCheckbox() {
    if (this.checkboxChangeHandler) {
      this.checkboxInput.removeEventListener('change', this.checkboxChangeHandler)
      this.checkboxChangeHandler = null
    }
  }

  showModal() {
    this.modal.classList.remove('hidden')
    this.modal.classList.add('block', 'md:flex')
  }

  hideModal() {
    this.modal.classList.remove('block', 'md:flex')
    this.modal.classList.add('hidden')
  }
}
